// Where the statutory right of withdrawal ("cooling-off") applies to a digital
// purchase, and therefore where checkout has to collect an express consent
// before it can deliver immediately.
//
// WHY THIS EXISTS. Under the EU Consumer Rights Directive (2011/83/EU, Art. 16(m))
// and the UK Consumer Contracts Regulations 2013 (reg. 37), a consumer buying
// digital content online has 14 days to withdraw for any reason. That right is
// lost ONLY if, before delivery begins, the consumer gives express prior consent
// to immediate performance AND acknowledges that they thereby lose it. Everything
// on supa.place is delivered the instant payment confirms, so without that consent
// every EU/UK buyer would keep an unconditional 14-day refund right no matter what
// the seller's published policy says.
//
// The consent is a GATE, not a disclosure: it must be an affirmative act, so the
// checkbox ships unticked and the pay button stays disabled until it is ticked.
// Pre-ticking it, or burying it in the terms, does not satisfy either instrument.
//
// SCOPE. EU-27 plus the UK. The EEA three (Iceland, Norway, Liechtenstein) have
// transposed the same directive and are included, because being wrong in the
// direction of asking for consent we did not need costs nothing, while being
// wrong the other way hands back a refund we thought was out of window.
//
// This is deliberately NOT the tax gate. `taxEnabledForCountry()` answers "are we
// registered to collect VAT here", which is a commercial decision we can switch
// per region; the withdrawal right applies whether or not we collect a cent of
// tax in that country, so the two lists must not be shared.

/**
 * Countries whose consumers hold a statutory right of withdrawal over digital
 * content. EU-27 + EEA-3 + GB.
 */
export const WITHDRAWAL_RIGHT_COUNTRIES: ReadonlySet<string> = new Set([
  // EU-27
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // EEA, same directive transposed
  'IS', 'NO', 'LI',
  // United Kingdom, Consumer Contracts Regulations 2013
  'GB',
]);

/** The withdrawal period, in days. Identical under both instruments. */
export const WITHDRAWAL_PERIOD_DAYS = 14;

/**
 * The exact wording the buyer ticks, kept here rather than inline in the checkout
 * so the server can store the sentence the buyer actually agreed to alongside the
 * order. In a dispute the question is not whether a box existed but what it said,
 * and a screenshot of today's checkout is not evidence about a sale made months
 * ago. Mirrored in functions/src/lib/withdrawal-right.ts (two runtimes, two
 * builds) and locked to it by src/lib/public-copy.test.ts.
 */
export const WITHDRAWAL_CONSENT_STATEMENT =
  'I want my purchase delivered immediately, and I accept that once I start a download or open ' +
  `an access link, I lose my ${WITHDRAWAL_PERIOD_DAYS}-day right to cancel. This does not affect my rights ` +
  'if my purchase is faulty or not as described, or the seller’s own refund policy.';

/**
 * Stamp for the wording above. BUMP IT whenever the statement changes, and never
 * re-use one: orders keep the version they were sold under, so a repeated version
 * across two different sentences makes every record under it ambiguous.
 */
export const WITHDRAWAL_CONSENT_VERSION = '2026-10-29';

/**
 * Whether a buyer in this country must tick the immediate-performance consent
 * before the order can be paid.
 *
 * An unknown or empty country returns false: the checkout resolves the country
 * server-side from the request IP before it will price an order, so there is no
 * state in which we both need the consent and do not know where the buyer is.
 * Gating on an unresolved country would block every checkout that had not
 * finished its quote yet.
 *
 * @param {string | null | undefined} country ISO 3166-1 alpha-2, any case.
 * @return {boolean} True when the consent gate applies.
 */
export function requiresWithdrawalConsent(country: string | null | undefined): boolean {
  if (!country) return false;
  return WITHDRAWAL_RIGHT_COUNTRIES.has(country.trim().toUpperCase());
}
